import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { StatutPeriodeEvaluation } from 'app/shared/model/periode-evaluation.model';
import { BulletinRow } from './bulletins.types';

export type EvaluationLite = {
  id: string;
  titre: string;
  bareme?: number;
  matiereId?: string;
  sousMatiereId?: string;
  periodeId?: string;
  type?: string;
  classeId?: string;
};

export type NoteRow = {
  inscriptionId: string;
  eleveId: string;
  nom: string;
  prenom: string;
  matricule: string;
  valeur?: number | null;
  absent?: boolean;
  appreciation?: string;
};

export type NotesStatus = 'Non saisie' | 'En cours' | 'Terminée';

export type NoteDetailItem = {
  evaluationId: string;
  evaluationTitre: string;
  type: string;
  bareme: number | null;
  matiereId: string;
  valeur: number | null;
  absent: boolean | null;
};

type NoteApiItem = {
  inscriptionId: string;
  valeur?: number | null;
  absent?: boolean;
  appreciation?: string;
  eleveId: string;
};

type PreviewSubject = {
  id: string;
  nom: string;
};

type MatiereStat = {
  devoirsSum: number;
  devoirsCount: number;
  compositionSum: number;
  compositionCount: number;
};

export type PreviewRow = {
  inscriptionId: string;
  eleveId: string;
  matricule: string;
  nom: string;
  prenom: string;
  matieres: Record<string, MatiereStat>;
};

type PreviewDetailItem = {
  evaluationTitre: string;
  note: string;
};

export type PreviewMatiereDetail = {
  matiereId: string;
  matiereNom: string;
  devoirs: PreviewDetailItem[];
  compositions: PreviewDetailItem[];
};

type OpenBulletinPdfPayload = {
  url: string;
  fileName: string;
  params: Record<string, string>;
};

type EvaluationNotesPayload = {
  evaluation: EvaluationLite;
  rows: NoteRow[];
  notesStatus: NotesStatus;
  periodeStatut: StatutPeriodeEvaluation | null;
};

const initialState = {
  loading: false,
  errorMessage: null as string | null,
  updating: false,
  evaluations: [] as EvaluationLite[],
  evaluationDetail: null as EvaluationLite | null,
  noteRows: [] as NoteRow[],
  notesStatus: 'Non saisie' as NotesStatus,
  periodeStatut: null as StatutPeriodeEvaluation | null,
  notesLoading: false,
  bulletins: [] as BulletinRow[],
  bulletinsLoading: false,
  previewRows: [] as PreviewRow[],
  previewMatieres: [] as PreviewSubject[],
  detailsByInscription: {} as Record<string, PreviewMatiereDetail[]>,
  previewLoading: false,
  noteDetailItems: [] as NoteDetailItem[],
  detailLoading: false,
  downloading: false,
};

const downloadBlob = (data: BlobPart, fileName: string, type = 'application/pdf') => {
  const blobUrl = window.URL.createObjectURL(new Blob([data], { type }));
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 0);
};

const openBlobInBrowser = (data: BlobPart, type = 'application/pdf') => {
  const blobUrl = window.URL.createObjectURL(new Blob([data], { type }));
  const openedWindow = window.open(blobUrl, '_blank', 'noopener,noreferrer');
  window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
  return Boolean(openedWindow);
};

const fetchEvaluationNotesPayload = async (evaluationId: string): Promise<EvaluationNotesPayload> => {
  const evaluationResponse = await axios.get<EvaluationLite>(`/api/evaluations/${evaluationId}`);
  const evaluation = evaluationResponse.data;

  const [periodeResponse, elevesResp, notesResp, statusResp] = await Promise.all([
    evaluation?.periodeId ? axios.get(`/api/periode-evaluations/${evaluation.periodeId}`) : Promise.resolve({ data: null }),
    axios.get<NoteRow[]>(`/api/notes/evaluation/${evaluationId}/eleves`),
    axios.get<NoteApiItem[]>('/api/notes', { params: { evaluationId } }),
    axios.get(`/api/notes/evaluation/${evaluationId}/status`),
  ]);

  const notes = notesResp.data || [];
  const rows = (elevesResp.data || []).map(eleve => {
    const existing = notes.find(note => note.inscriptionId === eleve.inscriptionId);
    return {
      ...eleve,
      valeur: existing?.valeur ?? null,
      absent: existing?.absent ?? false,
      appreciation: existing?.appreciation ?? '',
    };
  });

  return {
    evaluation,
    rows,
    notesStatus: (statusResp?.data?.statut as NotesStatus) || 'Non saisie',
    periodeStatut: (periodeResponse?.data?.statut as StatutPeriodeEvaluation | null) || null,
  };
};

export const getEvaluationDetail = createAsyncThunk(
  'note/fetch_evaluation_detail',
  async (evaluationId: string) => {
    const response = await axios.get<EvaluationLite>(`/api/evaluations/${evaluationId}`);
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const getEvaluationsByClasseAndPeriode = createAsyncThunk(
  'note/fetch_evaluations_by_classe_and_periode',
  async ({ classeId, periodeId }: { classeId: string; periodeId: string }) => {
    const response = await axios.get<EvaluationLite[]>('/api/evaluations', {
      params: { classeId, periodeId },
    });
    return response.data || [];
  },
  { serializeError: serializeAxiosError }
);

export const getEvaluationNotes = createAsyncThunk(
  'note/fetch_evaluation_notes',
  async (evaluationId: string) => fetchEvaluationNotesPayload(evaluationId),
  { serializeError: serializeAxiosError }
);

export const saveNotesBatch = createAsyncThunk(
  'note/save_notes_batch',
  async ({ evaluationId, rows }: { evaluationId: string; rows: NoteRow[] }) => {
    await axios.put(
      '/api/notes/batch',
      rows.map(row => ({
        evaluationId,
        inscriptionId: row.inscriptionId,
        eleveId: row.eleveId,
        valeur: row.absent ? null : row.valeur,
        absent: row.absent,
        appreciation: row.appreciation,
      }))
    );
    return fetchEvaluationNotesPayload(evaluationId);
  },
  { serializeError: serializeAxiosError }
);

export const getBulletins = createAsyncThunk(
  'note/fetch_bulletins',
  async ({ periodeId, classeId }: { periodeId: string; classeId?: string | null }) => {
    const response = await axios.get<BulletinRow[]>('/api/bulletins', {
      params: {
        periodeId,
        ...(classeId ? { classeId } : {}),
      },
    });
    return response.data || [];
  },
  { serializeError: serializeAxiosError }
);

export const downloadBulletinPdf = createAsyncThunk(
  'note/download_bulletin_pdf',
  async ({ url, fileName, params }: OpenBulletinPdfPayload) => {
    const response = await axios.get(url, {
      params,
      responseType: 'blob',
    });
    const source = String(response.headers['x-bulletin-source'] || 'draft').toLowerCase();
    if (source === 's3') {
      downloadBlob(response.data, fileName, 'application/pdf');
      return { fileName, downloaded: true, opened: false };
    }

    const opened = openBlobInBrowser(response.data, 'application/pdf');
    return { fileName, downloaded: false, opened };
  },
  { serializeError: serializeAxiosError }
);

export const getNoteDetailsByInscription = createAsyncThunk(
  'note/fetch_note_details_by_inscription',
  async ({ inscriptionId, periodeId }: { inscriptionId: string; periodeId: string }) => {
    const response = await axios.get<NoteDetailItem[]>('/api/notes/inscription', {
      params: { inscriptionId, periodeId },
    });
    return response.data || [];
  },
  { serializeError: serializeAxiosError }
);

export type NoteState = Readonly<typeof initialState>;

const NoteSlice = createSlice({
  name: 'note',
  initialState: initialState as NoteState,
  reducers: {
    clearNoteEditorState(state) {
      state.evaluationDetail = null;
      state.noteRows = [];
      state.notesStatus = 'Non saisie';
      state.periodeStatut = null;
    },
    clearBulletinsState(state) {
      state.bulletins = [];
    },
    clearPreviewState(state) {
      state.previewRows = [];
      state.previewMatieres = [];
      state.detailsByInscription = {};
    },
    clearNoteDetailState(state) {
      state.noteDetailItems = [];
    },
  },
  extraReducers(builder) {
    builder
      .addMatcher(isFulfilled(getEvaluationDetail), (state, action) => {
        state.loading = false;
        state.evaluationDetail = action.payload;
      })
      .addMatcher(isFulfilled(getEvaluationsByClasseAndPeriode), (state, action) => {
        state.loading = false;
        state.evaluations = action.payload;
      })
      .addMatcher(isFulfilled(getEvaluationNotes, saveNotesBatch), (state, action) => {
        state.notesLoading = false;
        state.updating = false;
        if (!action.payload) {
          return;
        }
        state.evaluationDetail = action.payload.evaluation;
        state.noteRows = action.payload.rows;
        state.notesStatus = action.payload.notesStatus;
        state.periodeStatut = action.payload.periodeStatut;
      })
      .addMatcher(isFulfilled(getBulletins), (state, action) => {
        state.bulletinsLoading = false;
        state.bulletins = action.payload;
      })
      .addMatcher(isFulfilled(getNoteDetailsByInscription), (state, action) => {
        state.detailLoading = false;
        state.noteDetailItems = action.payload;
      })
      .addMatcher(isFulfilled(downloadBulletinPdf), state => {
        state.downloading = false;
      })
      .addMatcher(isPending(getEvaluationDetail, getEvaluationsByClasseAndPeriode), state => {
        state.loading = true;
        state.errorMessage = null;
      })
      .addMatcher(isPending(getEvaluationNotes), state => {
        state.notesLoading = true;
        state.errorMessage = null;
      })
      .addMatcher(isPending(saveNotesBatch), state => {
        state.updating = true;
        state.errorMessage = null;
      })
      .addMatcher(isPending(getBulletins), state => {
        state.bulletinsLoading = true;
        state.errorMessage = null;
      })
      .addMatcher(isPending(getNoteDetailsByInscription), state => {
        state.detailLoading = true;
        state.errorMessage = null;
      })
      .addMatcher(isPending(downloadBulletinPdf), state => {
        state.downloading = true;
        state.errorMessage = null;
      })
      .addMatcher(
        isRejected(
          getEvaluationDetail,
          getEvaluationsByClasseAndPeriode,
          getEvaluationNotes,
          saveNotesBatch,
          getBulletins,
          getNoteDetailsByInscription,
          downloadBulletinPdf
        ),
        (state, action) => {
          state.loading = false;
          state.notesLoading = false;
          state.updating = false;
          state.bulletinsLoading = false;
          state.previewLoading = false;
          state.detailLoading = false;
          state.downloading = false;
          state.errorMessage = action.error.message || null;
        }
      );
  },
});

export const { clearNoteEditorState, clearBulletinsState, clearPreviewState, clearNoteDetailState } = NoteSlice.actions;

export default NoteSlice.reducer;
